import { useState, useEffect, useRef } from "react";
import { MessageCircle, ArrowUp, X, Send, Bot } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { streamChat, getSiteSettings } from "../lib/api";

const DEFAULT_WHATSAPP = "919324404572";
const WA_ENQUIRY = "Hi Manav, I'm interested in your online coaching. I'd like to know which coaching option would be best for me.";
const buildWaUrl = (num) => `https://wa.me/${(num || DEFAULT_WHATSAPP).replace(/[^\d]/g, "")}?text=${encodeURIComponent(WA_ENQUIRY)}`;

export default function FloatingWidgets() {
  const [showTop, setShowTop] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [waUrl, setWaUrl] = useState(buildWaUrl(DEFAULT_WHATSAPP));

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    getSiteSettings().then((s) => { if (s?.whatsapp_number) setWaUrl(buildWaUrl(s.whatsapp_number)); }).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* WhatsApp — sticky CTA with pre-filled message */}
      <a
        href={waUrl}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-float"
        aria-label="Chat with Manav on WhatsApp"
        style={{ bottom: "calc(1.5rem + env(safe-area-inset-bottom))", background: "#25D366" }}
        className="fixed left-4 md:left-6 z-40 flex items-center gap-2 rounded-full pl-4 pr-5 py-3 shadow-lg hover:scale-105 transition-transform text-white font-semibold text-sm"
      >
        <span
          aria-hidden
          className="absolute inset-0 rounded-full animate-pulsering -z-10 pointer-events-none"
          style={{ background: "#25D366" }}
        />
        <MessageCircle className="w-5 h-5" />
        <span className="hidden sm:inline whitespace-nowrap">Chat on WhatsApp</span>
      </a>

      {/* Back to top */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            data-testid="back-to-top"
            aria-label="Back to top"
            className="fixed bottom-24 right-6 z-40 w-11 h-11 rounded-full glass flex items-center justify-center hover:text-accent transition-colors"
          >
            <ArrowUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* AI Chat launcher */}
      <button
        onClick={() => setChatOpen(true)}
        data-testid="ai-chat-launcher"
        aria-label="Open AI assistant"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-accent flex items-center justify-center shadow-lg hover:scale-110 transition-transform"
      >
        <Bot className="w-7 h-7 text-white" />
        <span className="absolute inset-0 rounded-full bg-accent animate-pulsering -z-10 pointer-events-none" />
      </button>

      <AIChat open={chatOpen} onClose={() => setChatOpen(false)} />
    </>
  );
}

function AIChat({ open, onClose }) {
  const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2));
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hey! I'm Coach AI 👋 Ask me about training, nutrition, or which program fits your goals." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      await streamChat({ session_id: sessionId, message: text }, (token) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
          return copy;
        });
      });
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I'm offline right now. Please apply for coaching directly!" };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ai-chat-window"
      className={`fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[520px] rounded-3xl glass overflow-hidden flex flex-col shadow-2xl transition-all duration-300 ${
        open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-6 pointer-events-none"
      }`}
    >
      <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-accent">
        <div className="flex items-center gap-2 text-white">
          <Bot className="w-5 h-5" />
          <div>
            <p className="font-heading font-bold text-sm uppercase tracking-wide leading-none">Coach AI</p>
            <p className="text-[10px] text-white/80 mt-1">Powered by Manav Fit Coach</p>
          </div>
        </div>
        <button onClick={onClose} data-testid="ai-chat-close" aria-label="Close chat" className="text-white/90 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
              m.role === "user" ? "bg-accent text-white rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"
            }`}>
              {m.content || <span className="inline-flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" /><span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" style={{animationDelay:"0.2s"}}/><span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" style={{animationDelay:"0.4s"}}/></span>}
            </div>
          </div>
        ))}
      </div>

      <form onSubmit={send} className="p-3 border-t border-border flex items-center gap-2">
        <input
          data-testid="ai-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about training, diet, programs..."
          className="flex-1 bg-secondary rounded-full px-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-accent"
        />
        <button data-testid="ai-chat-send" disabled={loading}
          className="w-10 h-10 shrink-0 rounded-full bg-accent text-white flex items-center justify-center disabled:opacity-50">
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
